import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';

import {JoinOrganizationRecommendationsQuery} from './__generated/JoinOrganizationRecommendationsQuery';
import {JOIN_ORGANIZATION_LISTING_ORGANIZATION_FRAGMENT} from './JoinOrganizationListing';
import {
  JoinOrganizationSearchBaseProps,
  JoinOrganizationSearchLoading,
  PresentationalJoinOrganizationSearch,
} from './JoinOrganizationSearch';

export const JOIN_ORGANIZATION_RECOMMENDATIONS_QUERY: DocumentNode = gql`
  query JoinOrganizationRecommendationsQuery @skipAudit @engComponent(name: IDENTITY) {
    currentUser {
      ... on UserNode {
        id
        institution {
          id
        }
        organizations {
          id
        }
        recommendations {
          ...JoinOrganizationListingOrganization
        }
        referrers {
          id
        }
      }
    }
  }
  ${JOIN_ORGANIZATION_LISTING_ORGANIZATION_FRAGMENT}
`;

function getRecommendationsHeaderText(
  hasRecommendations: boolean,
  hasInstitution: boolean,
  hasReferrers: boolean
): string | undefined {
  if (!hasRecommendations) {
    return undefined;
  }
  return hasInstitution || hasReferrers
    ? 'Organizations you may be interested in:'
    : 'Recently joined organizations:';
}

export default function JoinOrganizationRecommendationsListing(
  props: JoinOrganizationSearchBaseProps & {
    onClickRequestJoinOrganization: (organizationId: string) => void;
  }
): JSX.Element | null {
  const queryResult = useBenchlingQuery<JoinOrganizationRecommendationsQuery>(
    JOIN_ORGANIZATION_RECOMMENDATIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load organization recommendations; try reloading the page.');
    return null;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return <JoinOrganizationSearchLoading {...props} />;
  } else {
    assert(
      queryResult.data.currentUser?.__typename === 'UserNode' || queryResult.data.currentUser == null,
      `Non-user should not access this page.`
    );

    const recommendations = queryResult.data.currentUser?.recommendations ?? [];
    const currentUser = queryResult.data.currentUser;
    const hasInstitution = queryResult.data.currentUser?.institution != null;
    const hasReferrers = currentUser != null && currentUser.referrers.length > 0;
    const hasRecommendations = recommendations.length !== 0;

    return (
      <PresentationalJoinOrganizationSearch
        {...props}
        currentUser={currentUser}
        organizations={recommendations}
        isLoading={false}
        showInstitutionFilter={props.showInstitutionFilter ?? false}
        headerText={getRecommendationsHeaderText(hasRecommendations, hasInstitution, hasReferrers)}
      />
    );
  }
}
